import type {
  CustomBlockDeployedEntity,
  CustomBlockEntity,
} from '@unifyapps/defs/blocks/Custom/types';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import {
  useBulkFetchEntitySnapshots,
  useSearchDeployedCustomComponents,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo } from 'react';
import {
  collectCustomBlockVersions,
  getCustomBlocksEntitySnapshotsFetchRequest,
  getCustomBlocksQueryFilter,
} from './utils';

/**
 * Multi-page variant of useFetchCustomBlocks. Walks every page returned from an
 * embedded-dependents response, unions their `customBlocksVersionsMap` entries,
 * and fires a single search + a single bulk-snapshot fetch keyed on the union.
 * This keeps consumers from issuing N separate requests when the page graph has
 * nested pages.
 */
function useFetchCustomBlocksForPages(pages: (InterfacePageEntity | undefined)[]) {
  const customBlockVersions = useMemo(() => collectCustomBlockVersions(pages), [pages]);

  const {
    data: customBlocks,
    isLoading: isCustomBlockLoading,
    error: errorCustomBlocks,
  } = useSearchDeployedCustomComponents(getCustomBlocksQueryFilter(customBlockVersions), {
    query: {
      select: (customBlockHit) => customBlockHit.objects as CustomBlockEntity[] | undefined,
      enabled: customBlockVersions.length > 0,
      staleTime: Infinity,
    },
  });

  const {
    data: customBlocksSnapshots,
    isLoading: isLoadingCustomBlocksSnapshots,
    error: errorCustomBlocksSnapshots,
  } = useBulkFetchEntitySnapshots(getCustomBlocksEntitySnapshotsFetchRequest(customBlockVersions), {
    query: {
      select: (data) =>
        Object.values(data.entityTypeVsEntities?.[UAEntityTypes.CustomBlock] || {}) as
          | CustomBlockDeployedEntity[]
          | undefined,
      enabled: customBlockVersions.length > 0,
      staleTime: Infinity,
    },
  });

  return {
    customBlocks,
    customBlocksSnapshots,
    isLoading: isCustomBlockLoading || isLoadingCustomBlocksSnapshots,
    error: errorCustomBlocks || errorCustomBlocksSnapshots,
  };
}

export default useFetchCustomBlocksForPages;
